/**
 * NotesContainer Component
 *
 * Features:
 * - Displays all notes for a lesson and allows creating new notes.
 * - Integrates with video.js to link notes to video timestamps.
 * - Handles note creation, editing, and deletion.
 * - Passes relevant props to child components for note data and actions.
 * - Uses Material-UI and CSS modules for styling.
 *
 * @param {Object} props - Component properties
 * @param {number} [props.idLesson] - Lesson ID for which to display notes
 * @param {number} [props.view] - View mode (1 = show notes, other = error message)
 * @returns {JSX.Element} Rendered NotesContainer component
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import videojs from 'video.js';

import TextField from '@mui/material/TextField';

import { getNotes, saveNote } from 'store/lesson-actions';

import Note from './Note/Note';
import Loading from 'components/UI/Loading/Loading';

import classes from './NotesContainer.module.css';

let myPlayer = null;

const NotesContainer = props => {
  const { t } = useTranslation(['notes']);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const dataAcademy = useSelector(state => state.academyInfo);
  const dataLesson = useSelector(state => state.lessonInfo);

  useEffect(() => {
    dispatch(getNotes(props.idLesson, dataAcademy.data.shortName));
    if (props.view === 1) {
      myPlayer = videojs('lessonCourse');
    }
  }, []);

  useEffect(() => {
    if (dataLesson.notes) {
      setIsLoading(false);
    }
  }, [dataLesson.notes]);

  const handleChangeNote = event => {
    myPlayer.pause();
    setNewNote(event.target.value);
  };

  const saveNoteHandler = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      (async () => {
        const newNoteData = await saveNote(
          props.idLesson,
          Math.round(myPlayer.currentTime()),
          newNote
        );
        if (newNoteData) {
          setNewNote('');
          myPlayer.play();
          dispatch(
            getNotes(props.idLesson, dataAcademy.data.shortName)
          );
        }
      })();
    }
  };

  return (
    <div className={classes.notesContainer}>
      {props.view === 1 ? (
        !isLoading ? (
          <>
            {/* Material-UI TextField for new note input */}
            <TextField
              value={newNote}
              onChange={handleChangeNote}
              label={t('take_note')}
              className={classes.newNoteInput}
              multiline
              rows={2}
              onKeyPress={saveNoteHandler}
            />
            {/* Custom component: Note receives noteData, idLesson, academyName */}
            {dataLesson.notes.map(note => (
              <Note
                key={note.idEntradaApunte}
                noteData={note}
                idLesson={props.idLesson}
                academyName={dataAcademy.data.shortName}
              />
            ))}
          </>
        ) : (
          <Loading />
        )
      ) : (
        <p className={classes.errorMessage}>{t('not_show_notes')}</p>
      )}
    </div>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 */
NotesContainer.propTypes = {
  /** Lesson ID for which to display notes */
  idLesson: PropTypes.number,
  /** View mode (1 = show notes, other = error message) */
  view: PropTypes.number
};

NotesContainer.defaultProps = {
  idLesson: undefined,
  view: undefined
};

export default NotesContainer;
